import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Chat, ChatContainer, ChatProps } from '@speeki/chat-web-components';
import { useAuthenticationContext, useMessenger } from '@speeki/firebase-chat';
import { FileType } from '@speeki/firebase-chat/build/types/types';
import { notify } from '@speeki/global-ui-components';

import { ChatTranslateDocument } from '@graphql/generated/graphql';

import { useGetChatUser } from '@utils/hooks/useGetChatUser';

import { useMutation, useReactiveVar } from '@apollo/client';
import { currentLanguageVar } from '@boot/reactiveVariables';
import { useReplying } from '@domains/Chat/ChatElement/useReplying';
import { useUserActivity } from '@domains/Chat/ChatElement/useUserActivity';
import { CustomMessageFile } from '@domains/Chat/CustomMessageFile/CustomMessageFile';

interface ChatElementProps extends Pick<
    ChatProps,
    | 'defaultOpenChat'
    | 'inputGrowsWithPanel'
    | 'maxInputLength'
    | 'maxInputRows'
    | 'resizable'
    | 'resizeStorage'
    | 'resizeStorageKey'
    | 'resizeTopInset'
    | 'showInputCounter'
> {
    chatId: string;
    participantName?: string;
}

export const ChatElement = ({
    chatId,
    participantName,
    ...props
}: ChatElementProps) => {
    const {
        chatMessages,
        conversation,
        fileUpload,
        getParticipant,
        isLoadingEarlier,
        loadMoreMessages,
        retryFileUpload,
        retryMessage,
        sendMessage,
        setLastRead,
        setParticipantsConversationMeta,
        speekiUser,
        useWatchForChat,
    } = useMessenger(chatId);
    const { authMetadata } = useAuthenticationContext();
    const { t } = useTranslation();
    const { onCancel, onReply, replying, resetReplying } = useReplying();
    const { getActivity } = useUserActivity();
    const [getChatUser] = useGetChatUser();
    const currentLocale =
        useReactiveVar(currentLanguageVar).toLocaleLowerCase();

    const [translateMessage] = useMutation(ChatTranslateDocument, {
        context: {
            firebaseToken: authMetadata?.token,
            notifyOnError: true,
        },
    });

    useWatchForChat();

    const getAndInjectUsersMetadata = useCallback(() => {
        conversation?.participants?.forEach(async (userUuid) => {
            if (userUuid === speekiUser?.uuid) return;

            if (participantName) {
                const participantNameSplit = participantName.split(' ');
                setParticipantsConversationMeta({
                    [userUuid]: {
                        firstName: participantNameSplit[0] ?? '',
                        lastName: participantNameSplit[1] ?? '',
                    },
                });
            } else if (!userUuid.startsWith('GUEST')) {
                const metadata = await getChatUser({ uuid: userUuid });
                setParticipantsConversationMeta({
                    [userUuid]: metadata.getUserByUuid,
                });
            }
        });
    }, [JSON.stringify(conversation?.participants)]);

    const hasNextPage = conversation?.metadata?.hasNextPage;

    useEffect(getAndInjectUsersMetadata, [
        JSON.stringify(conversation?.participants),
    ]);

    const onTranslate = async (text: string) => {
        const { data } = await translateMessage({
            onCompleted: ({ translate }) => {
                if (translate.length) return translate[0];
            },
            variables: {
                input: {
                    targetLanguage: currentLanguageVar(),
                    text: [text],
                },
            },
        });

        if (data?.translate.length) return data?.translate[0];
    };

    return (
        <ChatContainer>
            <Chat
                chatId={chatId}
                className="htmlToImageIgnore"
                defaultOpenChat
                getActivity={getActivity}
                getReplyUser={getParticipant}
                header={{
                    title: t('CHAT'),
                    unreadIndicator: conversation?.status === 'new',
                }}
                infiniteScrollArgs={{
                    hasNextPage: !!hasNextPage,
                    loading: isLoadingEarlier,
                    onLoadMore: () => void loadMoreMessages(),
                }}
                key={chatId}
                lastMessageId={
                    conversation?.lastMessageSent?.messageReference?.id
                }
                maxInputLength={1800}
                messages={chatMessages.reverse()}
                user={
                    speekiUser
                        ? {
                              _id: speekiUser.uuid,
                              chatId: chatId,
                              name: `${speekiUser.firstName} ${speekiUser.lastName}`,
                              type: speekiUser.type,
                          }
                        : undefined
                }
                {...(replying && {
                    replying: {
                        message: replying.message,
                        onClose: onCancel,
                        replyingTo: replying.username,
                    },
                })}
                MessageFileComponent={CustomMessageFile}
                locale={currentLocale}
                onFileError={(message) => {
                    notify(message).ERROR();
                }}
                onMessageSubmit={({ resetInput, text }) => {
                    void sendMessage(text, replying?.id);
                    resetInput();
                    resetReplying();
                }}
                onRead={setLastRead}
                onReply={(message) =>
                    onReply({
                        id: message._id as string,
                        message: message.text,
                        username: message.user.name || '',
                    })
                }
                onRetry={({ _id, text }) => {
                    void retryMessage(_id.toString(), text, replying?.id);
                }}
                onRetryUpload={(message) =>
                    retryFileUpload(message._id.toString(), replying?.id)
                }
                onTranslate={onTranslate}
                onUpload={(file) =>
                    fileUpload(file as unknown as FileType, replying?.id)
                }
                onUploadPending={() => resetReplying()}
                {...props}
            />
        </ChatContainer>
    );
};
